import React from 'react';
import { useParams } from 'react-router-dom';
import PageTitle from '../PageTitle';
import CleaningTasks from './CleaningTasks';
import MaidServices from '../MaidServices';

const constructionTasks = [
  'Clearing out large debris such as wood, nails, drywall pieces, and other construction materials left behind after the project.',
  'Disposing of leftover building materials properly.',
  'Dusting off surfaces such as walls, ceilings, light fixtures, vents, and windowsills that have accumulated construction dust.',
  'Wiping down all flat surfaces including countertops, shelves, and cabinets to remove dust and dirt.',
  'Sweeping, vacuuming, and mopping hard floors (tile, wood, laminate) to remove construction dust and residue.',
  'Spot cleaning any stains or spills from the construction process.',
  'Deep cleaning carpets, if applicable, to remove dust and debris.',
  'Cleaning windows (interior and sometimes exterior) to remove construction dust, paint splatters, or adhesive residues.',
  'Cleaning any glass doors, mirrors, or other glass surfaces.',
  'Vacuuming or cleaning air vents and ducts to remove dust and debris that may have settled during construction.',
  'Ensuring air filters are clean to prevent dust from circulating through the space.',
  'Thoroughly cleaning and disinfecting bathrooms, kitchen areas, and high-touch surfaces to remove any germs or residues left behind.',
  'Wiping down appliances, fixtures, and fittings installed during the construction or renovation process.',
  'Polishing furniture, light fixtures, and other details to make sure everything looks fresh and clean.',
  'Checking for any construction residue that may need additional attention.',
  'Properly disposing of any trash or construction-related waste that may have been missed during previous cleanup steps.',
];

const residentialTasks = [
  'Dusting furniture, shelves, baseboards, light fixtures, and blinds.',
  'Wiping down high-touch surfaces like doorknobs, light switches, and countertops.',
  'Wiping down countertops, sinks, and cabinet exteriors.',
  'Cleaning appliances like the microwave, stove, and refrigerator (exterior and sometimes interior).',
  'Scrubbing the sink and faucet to remove grime and buildup.',
  'Scrubbing and disinfecting toilets, sinks, bathtubs, and showers.',
  'Cleaning mirrors and wiping down bathroom counters and fixtures.',
  'Mopping floors and wiping down baseboards.',
  'Sweeping and mopping hard floors (tile, hardwood, laminate).',
  'Vacuuming carpets, including edges and corners, and spot cleaning stains if necessary.',
  'Emptying trash bins in the kitchen, bathrooms, and other rooms and replacing liners.',
  'Straightening up common areas, arranging pillows, and organizing items as needed.',
  'Cleaning interior windows and glass surfaces, including mirrors, to remove streaks and fingerprints.',
  'Tidying up closets, drawers, and organizing spaces in the home as part of a more detailed cleaning session (optional).',
];

const movingTasks = [
  'Dusting: Dusting all surfaces including baseboards, light fixtures, and vents.',
  'Vacuuming: Thorough vacuuming of carpets and rugs, including edges and corners.',
  'Mopping: Sweeping and mopping hard floors (tile, hardwood, laminate).',
  'Appliance Cleaning: Deep cleaning inside and out of appliances like the oven, refrigerator, microwave, and dishwasher.',
  'Countertops & Cabinets: Wiping down all countertops and the exterior of cabinets, cleaning any spills or stains.',
  'Sink & Faucets: Scrubbing the sink, faucet, and any grime or food buildup.',
  'Toilet, Tub, and Shower: Scrubbing and sanitizing the toilet, bathtub, shower, and sink.',
  'Mirrors & Fixtures: Cleaning mirrors, light fixtures, and faucets.',
  'Floors & Baseboards: Mopping floors and wiping down baseboards.',
  'Windows: Cleaning interior windows and glass doors, removing dust, smudges, and streaks.',
  'Glass Fixtures: Cleaning glass surfaces like mirrors and glass shower doors.',
  'Inside & Out: Wiping down the interiors and exteriors of cabinets and drawers in kitchens, bathrooms, and other spaces.',
  'Emptying Bins: Removing all trash from the home, including any items left behind by previous occupants.',
  'Walls & Doors: Spot cleaning or wiping down walls and doors to remove any marks, smudges, or stains.',
  'Light Switches & Doorknobs: Cleaning and sanitizing high-touch areas.',
  'Detailing: Ensuring every corner, crevice, and surface is clean, including light fixtures and air vents.',
  'Ensuring that high-touch areas (like handles and switches) are sanitized for hygiene and safety.',
];

const deepCleaningTasks = [
  'Dusting all surfaces, including baseboards, crown molding, window sills, and ceiling fans.',
  'Dusting behind and underneath furniture and appliances.',
  'Cleaning light fixtures, chandeliers, and lamps.',
  'Appliances: Cleaning the inside and outside of appliances, including the oven, refrigerator, microwave, dishwasher, and stovetop.',
  'Cabinets: Wiping down the inside and outside of kitchen cabinets and drawers.',
  'Countertops: Scrubbing countertops, backsplash, and all kitchen surfaces to remove built-up grease or grime.',
  'Sink & Faucets: Deep cleaning and sanitizing sinks, faucets, and drains.',
  'Scrubbing and disinfecting the toilet, shower, bathtub, and sink.',
  'Cleaning and polishing bathroom fixtures like faucets, mirrors, and light fixtures.',
  'Removing soap scum, lime scale, and mold from tiles and grout.',
  'Wiping down and sanitizing counters, shelves, and other surfaces.',
  'Carpets & Rugs: Vacuuming carpets, including edges and corners, and steam cleaning or shampooing to remove deep stains and odors.',
  'Hard Floors: Sweeping, mopping, and scrubbing tile, hardwood, laminate, or other floors to remove grime, dirt, and stains.',
  'Cleaning all windows (interior and sometimes exterior) to remove smudges, dust, and streaks.',
  'Wiping down glass doors, mirrors, and any other glass surfaces.',
  'Moving furniture and appliances (when possible) to clean underneath and behind them, removing dust, debris, and dirt.',
  'Spot cleaning walls to remove stains, marks, or fingerprints.',
  'Cleaning ceiling corners, light fixtures, and air vents to remove cobwebs and dust.',
  'Thoroughly sanitizing high-touch areas like doorknobs, light switches, handles, and remote controls.',
  'Using disinfectants to ensure all surfaces are sanitized, especially in bathrooms and kitchens.',
  'Helping to declutter and organize spaces like closets, cupboards, and other areas, as part of a more thorough clean.',
  'Cleaning and polishing furniture, deep-cleaning upholstery, and treating any stains or dirt in furniture or textiles.',
];

const ServiceType = () => {
  const { id } = useParams<{ id: string }>();

  let title = '';

  switch (id) {
    case 'residential':
      title = 'Residential Cleaning';
      break;
    case 'commercial':
      title = 'Commercial Cleaning';
      break;
    case 'moving':
      title = 'Moving Cleaning';
      break;
    case 'construction':
      title = 'Construction Cleaning';
      break;
    case 'deep-cleaning':
      title = 'Deep Cleaning';
      break;
    case 'maid':
      title = 'Maid & Housekeeping Services';
      break;
    default:
      break;
  }

  return (
    <>
      <PageTitle title={title} />
      {id === 'residential' && <CleaningTasks tasks={residentialTasks} />}
      {id === 'construction' && <CleaningTasks tasks={constructionTasks} />}
      {id === 'moving' && <CleaningTasks tasks={movingTasks} />}
      {id === 'deep-cleaning' && <CleaningTasks tasks={deepCleaningTasks} />}
      {id === 'maid' && <MaidServices />}
    </>
  );
};

export default ServiceType;
